<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import {
  cortexConversationControlsAPI,
  cortexMemoryAPI,
  cortexProcessingRunsAPI,
} from 'dashboard/api/cortex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { usePolicy } from 'dashboard/composables/usePolicy';
import {
  cortexProcessingErrorMessage,
  cortexProcessingPreviewRequiresRefresh,
} from 'dashboard/helper/cortexProcessingError';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  contactId: {
    type: [Number, String],
    default: undefined,
  },
});

const { t } = useI18n();
const { checkPermissions } = usePolicy();

const isLoading = ref(false);
const isActing = ref(false);
const isPreviewing = ref(false);
const isStartingProcessing = ref(false);
const processingDialog = ref(null);
const processingPreview = ref(null);
const processingRun = ref(null);
const control = ref(null);
const readiness = ref(null);
const decision = ref(null);
const lastRun = ref(null);
const memoryContext = ref(null);
const memoryError = ref(null);
const canControl = computed(() =>
  checkPermissions([
    'administrator',
    'can_manage_cortex',
    'can_control_cortex_conversations',
  ])
);
const canProcess = computed(() =>
  checkPermissions([
    'administrator',
    'can_manage_cortex',
    'can_process_cortex_conversations',
  ])
);

const controlStatus = computed(() => {
  const cortexControl = control.value?.cortex_control || {};
  return cortexControl.status || 'active';
});

const readinessStatus = computed(() => readiness.value?.status || 'unknown');
const readinessChecks = computed(() => readiness.value?.checks || []);
const activeAgent = computed(() => readiness.value?.agent);
const isPaused = computed(() => controlStatus.value === 'paused');
const contactMemory = computed(() => memoryContext.value?.contact_profile);
const previousConversationSummary = computed(
  () => memoryContext.value?.previous_conversation_summary
);

const memoryText = value => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.text || value.summary?.text || value.summary || '';
};

const recentConversationSummaries = computed(() => {
  const summaries = memoryContext.value?.recent_conversation_summaries || [];
  const previousText = memoryText(previousConversationSummary.value);

  return summaries.filter(
    summary => memoryText(summary.summary) !== previousText
  );
});

const hasMemoryContext = computed(
  () =>
    contactMemory.value ||
    previousConversationSummary.value ||
    recentConversationSummaries.value.length
);

const statusLabel = status => {
  const labels = {
    active: t('CORTEX_CONVERSATION.STATUS.active'),
    paused: t('CORTEX_CONVERSATION.STATUS.paused'),
    ready: t('CORTEX_CONVERSATION.READINESS.ready'),
    blocked: t('CORTEX_CONVERSATION.READINESS.blocked'),
    warning: t('CORTEX_CONVERSATION.READINESS.warning'),
    unknown: t('CORTEX_CONVERSATION.READINESS.unknown'),
    passed: t('CORTEX_CONVERSATION.CHECK_STATUS.passed'),
    failed: t('CORTEX_CONVERSATION.CHECK_STATUS.failed'),
  };
  return labels[status] || status;
};

const checkLabel = key => {
  const labels = {
    cortex_feature: t('CORTEX_CONVERSATION.CHECKS.cortex_feature'),
    active_agent_inbox: t('CORTEX_CONVERSATION.CHECKS.active_agent_inbox'),
    active_agent: t('CORTEX_CONVERSATION.CHECKS.active_agent'),
    agent_bot_link: t('CORTEX_CONVERSATION.CHECKS.agent_bot_link'),
    conversation_control: t('CORTEX_CONVERSATION.CHECKS.conversation_control'),
    lgpd_consent: t('CORTEX_CONVERSATION.CHECKS.lgpd_consent'),
    knowledge_base: t('CORTEX_CONVERSATION.CHECKS.knowledge_base'),
  };
  return labels[key] || key;
};

const decisionLabel = action => {
  const labels = {
    respond: t('CORTEX_CONVERSATION.DECISIONS.respond'),
    request_consent: t('CORTEX_CONVERSATION.DECISIONS.request_consent'),
    stay_paused: t('CORTEX_CONVERSATION.DECISIONS.stay_paused'),
    idle: t('CORTEX_CONVERSATION.DECISIONS.idle'),
  };
  return labels[action] || t('CORTEX_CONVERSATION.NO_DECISION');
};

const checkClass = status => {
  if (status === 'passed') return 'bg-n-teal-3 text-n-teal-11';
  if (status === 'warning') return 'bg-n-amber-3 text-n-amber-11';
  return 'bg-n-ruby-3 text-n-ruby-11';
};

const fetchMemoryContext = async () => {
  if (!props.contactId) {
    memoryContext.value = null;
    memoryError.value = null;
    return;
  }

  try {
    const { data } = await cortexMemoryAPI.context({
      contact_id: props.contactId,
      conversation_id: props.conversationId,
    });
    memoryContext.value = data;
    memoryError.value = null;
  } catch (error) {
    memoryContext.value = null;
    memoryError.value =
      error?.response?.data?.blocking_reason ||
      error?.response?.data?.errors?.[0] ||
      t('CORTEX_CONVERSATION.MEMORY.UNAVAILABLE');
  }
};

const fetchState = async () => {
  if (!props.conversationId || !canControl.value) return;

  isLoading.value = true;
  try {
    const [controlResponse, readinessResponse, decisionResponse] =
      await Promise.all([
        cortexConversationControlsAPI.status(props.conversationId),
        cortexConversationControlsAPI.runtimeReadiness(props.conversationId),
        cortexConversationControlsAPI.runtimeDecision(props.conversationId),
      ]);
    control.value = controlResponse.data;
    readiness.value = readinessResponse.data;
    decision.value = decisionResponse.data;
    await fetchMemoryContext();
  } catch (error) {
    useAlert(t('CORTEX_CONVERSATION.API.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openProcessingPreview = async () => {
  isPreviewing.value = true;
  try {
    const { data } = await cortexProcessingRunsAPI.preview({
      filters: { conversation_id: props.conversationId },
    });
    processingPreview.value = data;
    processingDialog.value?.open();
  } catch (error) {
    useAlert(
      cortexProcessingErrorMessage(
        error,
        t,
        t('CORTEX_CONVERSATION.PROCESSING.PREVIEW_ERROR')
      )
    );
  } finally {
    isPreviewing.value = false;
  }
};

const startProcessing = async () => {
  isStartingProcessing.value = true;
  try {
    const idempotencyKey = `conversation-${props.conversationId}-${Date.now()}`;
    const { data } = await cortexProcessingRunsAPI.start(
      {
        preview_token: processingPreview.value.preview_token,
        authorization_reason: 'quality_review',
        maximum_cost: processingPreview.value.estimated_cost,
      },
      idempotencyKey
    );
    processingRun.value = data;
    processingDialog.value?.close();
    useAlert(t('CORTEX_CONVERSATION.PROCESSING.STARTED'));
  } catch (error) {
    if (cortexProcessingPreviewRequiresRefresh(error)) {
      processingDialog.value?.close();
      processingPreview.value = null;
    }
    useAlert(
      cortexProcessingErrorMessage(
        error,
        t,
        t('CORTEX_CONVERSATION.PROCESSING.START_ERROR')
      )
    );
  } finally {
    isStartingProcessing.value = false;
  }
};

const runAction = async action => {
  isActing.value = true;
  try {
    if (action === 'run') {
      const { data } = await cortexConversationControlsAPI.runtimeRun(
        props.conversationId,
        { input: { source: 'dashboard_manual' } }
      );
      lastRun.value = data;
      useAlert(t('CORTEX_CONVERSATION.API.RUN'));
    } else if (action === 'pause') {
      await cortexConversationControlsAPI.pause(props.conversationId);
      useAlert(t('CORTEX_CONVERSATION.API.PAUSE'));
    } else if (action === 'resume') {
      await cortexConversationControlsAPI.resume(props.conversationId);
      useAlert(t('CORTEX_CONVERSATION.API.RESUME'));
    } else if (action === 'handoff') {
      await cortexConversationControlsAPI.handoff(props.conversationId, {
        reason: 'dashboard_manual_handoff',
      });
      useAlert(t('CORTEX_CONVERSATION.API.HANDOFF'));
    }
    await fetchState();
  } catch (error) {
    useAlert(t('CORTEX_CONVERSATION.API.ACTION_ERROR'));
  } finally {
    isActing.value = false;
  }
};

watch(() => [props.conversationId, props.contactId], fetchState);
onMounted(fetchState);
</script>

<template>
  <div class="grid gap-4">
    <woot-loading-state v-if="isLoading" />
    <template v-else-if="canControl">
      <div class="grid gap-2">
        <div class="flex items-start justify-between gap-3">
          <div>
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ activeAgent?.name || t('CORTEX_CONVERSATION.NO_AGENT') }}
            </h3>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ t('CORTEX_CONVERSATION.DESCRIPTION') }}
            </p>
          </div>
          <span class="px-2 py-1 text-xs rounded-md bg-n-alpha-2">
            {{ statusLabel(controlStatus) }}
          </span>
        </div>
        <div class="flex items-center justify-between gap-3 text-sm">
          <span class="text-n-slate-11">
            {{ t('CORTEX_CONVERSATION.READINESS_LABEL') }}
          </span>
          <span class="font-medium text-n-slate-12">
            {{ statusLabel(readinessStatus) }}
          </span>
        </div>
        <div class="flex items-center justify-between gap-3 text-sm">
          <span class="text-n-slate-11">
            {{ t('CORTEX_CONVERSATION.DECISION_LABEL') }}
          </span>
          <span class="font-medium text-n-slate-12">
            {{ decisionLabel(decision?.action) }}
          </span>
        </div>
      </div>

      <div class="grid gap-2">
        <div
          v-for="check in readinessChecks"
          :key="check.key"
          class="flex items-center justify-between gap-3 text-sm"
        >
          <span class="text-n-slate-11">{{ checkLabel(check.key) }}</span>
          <span
            class="px-2 py-1 text-xs rounded-md"
            :class="checkClass(check.status)"
          >
            {{ statusLabel(check.status) }}
          </span>
        </div>
      </div>

      <div class="grid gap-2">
        <p class="text-xs font-medium text-n-slate-12">
          {{ t('CORTEX_CONVERSATION.MEMORY.TITLE') }}
        </p>
        <div
          v-if="hasMemoryContext"
          class="grid gap-2 p-3 border border-n-weak rounded-lg bg-n-alpha-1"
        >
          <div v-if="previousConversationSummary">
            <p class="text-xs font-medium text-n-slate-12">
              {{ t('CORTEX_CONVERSATION.MEMORY.PREVIOUS_SUMMARY') }}
            </p>
            <p class="mt-1 text-xs whitespace-pre-line text-n-slate-11">
              {{ memoryText(previousConversationSummary) }}
            </p>
          </div>
          <div v-if="contactMemory">
            <p class="text-xs font-medium text-n-slate-12">
              {{ t('CORTEX_CONVERSATION.MEMORY.CONTACT_MEMORY') }}
            </p>
            <p class="mt-1 text-xs whitespace-pre-line text-n-slate-11">
              {{ memoryText(contactMemory) }}
            </p>
          </div>
          <div v-if="recentConversationSummaries.length" class="grid gap-2">
            <p class="text-xs font-medium text-n-slate-12">
              {{ t('CORTEX_CONVERSATION.MEMORY.RECENT_SUMMARIES') }}
            </p>
            <div
              v-for="summary in recentConversationSummaries"
              :key="summary.conversation_id"
              class="grid gap-1 border-t border-n-weak pt-2 first:border-t-0 first:pt-0"
            >
              <p class="text-xs font-medium text-n-slate-11">
                {{
                  t('CORTEX_CONVERSATION.MEMORY.RECENT_ITEM_TITLE', {
                    id: summary.display_id || summary.conversation_id,
                  })
                }}
              </p>
              <p class="text-xs whitespace-pre-line text-n-slate-11">
                {{ memoryText(summary.summary) }}
              </p>
            </div>
          </div>
        </div>
        <p
          v-else
          class="p-3 text-xs border border-n-weak rounded-lg text-n-slate-11 bg-n-alpha-1"
        >
          {{
            memoryError
              ? t('CORTEX_CONVERSATION.MEMORY.BLOCKED')
              : t('CORTEX_CONVERSATION.MEMORY.EMPTY')
          }}
        </p>
      </div>

      <div class="grid grid-cols-2 gap-2">
        <NextButton
          type="button"
          sm
          blue
          :is-loading="isActing"
          @click="runAction('run')"
        >
          {{ t('CORTEX_CONVERSATION.ACTIONS.RUN') }}
        </NextButton>
        <NextButton
          type="button"
          sm
          slate
          outline
          :is-loading="isActing"
          @click="fetchState"
        >
          {{ t('CORTEX_CONVERSATION.ACTIONS.REFRESH') }}
        </NextButton>
        <NextButton
          v-if="isPaused"
          type="button"
          sm
          teal
          outline
          :is-loading="isActing"
          @click="runAction('resume')"
        >
          {{ t('CORTEX_CONVERSATION.ACTIONS.RESUME') }}
        </NextButton>
        <NextButton
          v-else
          type="button"
          sm
          amber
          outline
          :is-loading="isActing"
          @click="runAction('pause')"
        >
          {{ t('CORTEX_CONVERSATION.ACTIONS.PAUSE') }}
        </NextButton>
        <NextButton
          type="button"
          sm
          ruby
          outline
          :is-loading="isActing"
          @click="runAction('handoff')"
        >
          {{ t('CORTEX_CONVERSATION.ACTIONS.HANDOFF') }}
        </NextButton>
      </div>

      <div
        v-if="lastRun"
        class="p-3 border border-n-weak rounded-lg bg-n-alpha-1"
      >
        <p class="text-xs font-medium text-n-slate-12">
          {{ t('CORTEX_CONVERSATION.LAST_RUN') }}
        </p>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ lastRun.output?.status || lastRun.action }}
        </p>
      </div>
    </template>

    <div v-if="canProcess" class="grid gap-2">
      <NextButton
        type="button"
        sm
        blue
        outline
        icon="i-lucide-brain-circuit"
        data-testid="cortex-process-conversation"
        :is-loading="isPreviewing"
        :label="$t('CORTEX_CONVERSATION.PROCESSING.ACTION')"
        @click="openProcessingPreview"
      />
      <p
        v-if="processingRun"
        class="text-xs text-n-slate-11"
        aria-live="polite"
      >
        {{
          $t('CORTEX_CONVERSATION.PROCESSING.RUN_STATUS', {
            id: processingRun.id,
            status: processingRun.status,
          })
        }}
      </p>
    </div>

    <Dialog
      ref="processingDialog"
      width="md"
      :title="$t('CORTEX_CONVERSATION.PROCESSING.TITLE')"
      :confirm-button-label="$t('CORTEX_CONVERSATION.PROCESSING.CONFIRM')"
      :is-loading="isStartingProcessing"
      :disable-confirm-button="!processingPreview"
      @confirm="startProcessing"
    >
      <div
        v-if="processingPreview"
        class="grid gap-4 text-sm"
        data-testid="cortex-processing-preview"
      >
        <dl
          class="grid grid-cols-2 gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
        >
          <div>
            <dt class="text-xs text-n-slate-11">
              {{ $t('CORTEX_CONVERSATION.PROCESSING.CONVERSATIONS') }}
            </dt>
            <dd class="font-medium text-n-slate-12">
              {{ processingPreview.conversation_count }}
            </dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-11">
              {{ $t('CORTEX_CONVERSATION.PROCESSING.MESSAGES') }}
            </dt>
            <dd class="font-medium text-n-slate-12">
              {{ processingPreview.message_count }}
            </dd>
          </div>
          <div class="col-span-2">
            <dt class="text-xs text-n-slate-11">
              {{ $t('CORTEX_CONVERSATION.PROCESSING.ESTIMATED_COST') }}
            </dt>
            <dd class="font-medium text-n-slate-12">
              {{ processingPreview.estimated_cost }}
            </dd>
          </div>
        </dl>
        <p class="text-n-slate-11">
          {{ $t('CORTEX_CONVERSATION.PROCESSING.SNAPSHOT_NOTICE') }}
        </p>
        <p class="text-n-slate-11">
          {{ $t('CORTEX_CONVERSATION.PROCESSING.CONSENT_NOTICE') }}
        </p>
      </div>
    </Dialog>
  </div>
</template>
